import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Quote, GraduationCap, Wrench, Boxes as BoxesIcon } from "lucide-react";
import { PRODUCTS, COLORS, COMPANY } from "@/lib/site";
import { BESPOKE_DOMAINS, ACADEMY_COURSES } from "@/lib/preview";
import { Reveal } from "@/components/Reveal";

const Chip = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 backdrop-blur border border-gray-200 px-3 py-1 text-xs font-medium text-[#1A1A1A]">
    {children}
  </span>
);

const Hero = () => (
  <section className="bg-[#FDFCF0] relative overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{backgroundImage:"radial-gradient(circle at 20% 20%, rgba(0,132,61,0.10), transparent 40%), radial-gradient(circle at 80% 30%, rgba(0,51,160,0.10), transparent 45%)"}} />
    <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-16 md:pb-20 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <Reveal>
          <div className="inline-flex items-center gap-2 text-sm font-medium mb-6 px-3 py-1 rounded-full" style={{ backgroundColor: "#E7F3EC", color: COLORS.green }}>
            <Sparkles size={14} /> AI-first problem solving · Mysuru
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] text-[#1A1A1A]">
            AI, applied to <em className="italic text-[#00843D] not-italic-fallback">small business</em> problems.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg leading-relaxed text-slate-600 max-w-2xl">
            Aumonline helps Indian MSMEs solve real business problems using AI — as software you subscribe to, as bespoke solutions built for you, or as a course that teaches you to build them yourself.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-wrap gap-3">
            <Chip><BoxesIcon size={12} style={{color: COLORS.blue}} /> Solutions</Chip>
            <Chip><Wrench size={12} style={{color: COLORS.blue}} /> Bespoke</Chip>
            <Chip><GraduationCap size={12} style={{color: COLORS.blue}} /> Academy</Chip>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-white font-medium transition-colors duration-150 hover:opacity-90" style={{ backgroundColor: COLORS.blue }}>Bring us your problem <ArrowRight size={18} /></Link>
            <Link to="/products" className="inline-flex items-center gap-2 rounded-md px-6 py-3 font-medium border transition-colors duration-150 hover:bg-white/60" style={{ borderColor: COLORS.green, color: COLORS.green }}>See what we've solved</Link>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-5">
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Software running MSMEs" className="rounded-lg border border-gray-200 w-full h-[380px] object-cover shadow-sm" />
          <div className="absolute -bottom-5 -left-5 bg-white border border-gray-200 rounded-md px-4 py-3 shadow-sm max-w-[220px]">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Live pilot</div>
            <div className="text-sm font-semibold text-[#1A1A1A]">Wekreate Space</div>
            <div className="text-xs text-slate-600">5 hubs · 600+ seats · powered by Kreta</div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Pillars = () => (
  <section className="bg-white border-y border-gray-200" data-testid="pillars">
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
      <Reveal>
        <h2 className="font-serif-display text-3xl md:text-4xl font-semibold tracking-tight text-[#1A1A1A]">Three ways we solve.</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">Same practice — expressed as a subscription product, a bespoke build, or a course.</p>
      </Reveal>
      <div className="mt-10 grid md:grid-cols-3 gap-6">
        {[
          { icon: BoxesIcon, tag: "Solutions", title: "Software you subscribe to", desc: "Kreta, Apoopika and Jigyasa — live SaaS built for MSMEs. Try free for 14 days.", cta: "See our software", to: "/products" },
          { icon: Wrench, tag: "Bespoke", title: "Solved for your business", desc: "You bring the problem, we design and build the AI-powered solution — construction, clinics, events, service teams.", cta: "Start a conversation", to: "/contact" },
          { icon: GraduationCap, tag: "Academy", title: "Learn to build with AI", desc: "Short, practical courses drawn from our 6-month AI Generalist fellowship — from prompt engineering to shipping real products.", cta: "Join the interest list", to: "/contact" },
        ].map((p,i) => (
          <Reveal key={p.tag} delay={i*0.06}>
            <div className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
              <p.icon size={26} style={{color: COLORS.blue}} />
              <div className="mt-3 text-xs font-semibold uppercase tracking-wider" style={{color: COLORS.green}}>{p.tag}</div>
              <h3 className="mt-1 text-xl font-semibold text-[#1A1A1A]">{p.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed flex-1">{p.desc}</p>
              <Link to={p.to} className="mt-5 inline-flex items-center gap-2 font-medium hover:gap-3 transition-all duration-150" style={{color: COLORS.blue}}>{p.cta} <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Practitioner = () => (
  <section className="bg-[#0A0F1C] text-white relative overflow-hidden" data-testid="practitioner">
    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{backgroundImage:"radial-gradient(600px circle at 15% 30%, rgba(0,132,61,0.20), transparent 60%), radial-gradient(500px circle at 85% 70%, rgba(255,184,28,0.10), transparent 60%)"}} />
    <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{backgroundImage:"linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize:"48px 48px"}}/>
    <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center">
      <Reveal>
        <div className="inline-flex items-center gap-2 text-xs font-medium mb-4 px-3 py-1 rounded-full bg-white/10 text-white">
          <Sparkles size={14} /> The person behind Aumonline
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl font-semibold tracking-tight">Walk in with a problem. Walk out with a solution.</h2>
        <p className="mt-5 text-slate-300 leading-relaxed">
          Aumonline is led by Ashwath Ramaswamy — 30+ years being part of teams executing EPC contracts on offshore oil platforms. During the process he was technical architect for automating various business process in this domain. He has completed AI Generalist Fellowship course which he is now teaching. He has built three live SaaS products built and shipped, and an AMFI-registered mutual-fund distribution practice.
        </p>
        <p className="mt-4 text-slate-300 leading-relaxed">
          That combination — deep operations experience and hands-on AI capability — is what lets us walk into a real business problem and walk out with a working solution.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="grid grid-cols-2 gap-4">
          {[
            { k: "30+", v: "Years of EPC / project management" },
            { k: "6 mo.", v: "AI Generalist Fellowship completed" },
            { k: "3", v: "Live SaaS products shipped" },
            { k: "ARN-256095", v: "AMFI-registered MF distributor" },
          ].map((s,i) => (
            <div key={i} className="rounded-md border border-white/10 bg-white/5 p-5">
              <div className="text-3xl font-semibold font-serif-display">{s.k}</div>
              <div className="mt-1 text-xs text-slate-300 leading-snug">{s.v}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Solutions = () => (
  <section className="bg-[#FDFCF0]" data-testid="solutions">
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
      <Reveal>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider" style={{color: COLORS.green}}>Solutions</div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-semibold tracking-tight text-[#1A1A1A]">Live software, running real businesses.</h2>
          </div>
          <Link to="/products" className="inline-flex items-center gap-2 font-medium" style={{color: COLORS.blue}}>See all <ArrowRight size={16} /></Link>
        </div>
      </Reveal>
      <div className="mt-10 grid md:grid-cols-3 gap-6">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.key} delay={i * 0.06}>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col h-full">
              <img src={p.img} alt={p.name} className="h-40 w-full object-cover" />
              <div className="p-6 flex flex-col flex-1">
                <div className="text-xs font-medium uppercase tracking-wide" style={{ color: COLORS.green }}>{p.tag}</div>
                <div className="mt-1 flex items-center gap-2">
                  <p.icon size={20} style={{ color: COLORS.blue }} />
                  <h3 className="text-lg font-semibold text-[#1A1A1A]">{p.name}</h3>
                </div>
                <p className="mt-3 text-slate-600 leading-relaxed text-sm flex-1">{p.short}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Bespoke = () => (
  <section className="bg-white border-y border-gray-200" data-testid="bespoke">
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 grid lg:grid-cols-12 gap-10">
      <Reveal className="lg:col-span-5">
        <div className="text-xs font-semibold uppercase tracking-wider" style={{color: COLORS.green}}>Bespoke</div>
        <h2 className="font-serif-display text-3xl md:text-4xl font-semibold tracking-tight text-[#1A1A1A]">You bring the problem.<br/>We build the solution.</h2>
        <p className="mt-5 text-slate-600 leading-relaxed">Not every business fits a product. When it doesn't, we work with you to design an AI-powered solution and ship it — with the same engine we use for our own SaaS.</p>
        <div className="mt-6 rounded-lg border border-gray-200 p-5 bg-[#FDFCF0]">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Quote size={14} /> Flagship in progress</div>
          <div className="mt-2 font-semibold text-[#1A1A1A]">Nirmana — Prem Constructions</div>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">A construction platform for a 22-floor Bangalore build. Site, subcontractors, procurement and progress — one system.</p>
        </div>
      </Reveal>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {BESPOKE_DOMAINS.map((d, i) => (
          <Reveal key={d.name} delay={i*0.05}>
            <div className="bg-white rounded-lg border border-gray-200 p-5 h-full">
              <d.icon size={22} style={{color: COLORS.blue}} />
              <div className="mt-3 font-semibold text-[#1A1A1A]">{d.name}</div>
              <p className="mt-1 text-sm text-slate-600 leading-snug">{d.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Academy = () => (
  <section className="bg-[#0A0F1C] text-white relative overflow-hidden" data-testid="academy">
    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{backgroundImage:"radial-gradient(600px circle at 80% 20%, rgba(0,132,61,0.18), transparent 60%), radial-gradient(500px circle at 20% 80%, rgba(255,184,28,0.08), transparent 60%)"}} />
    <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{backgroundImage:"linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize:"48px 48px"}}/>
    <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
      <Reveal>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-[#7DDFA0]">Academy</div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-semibold tracking-tight">Learn the tools we build with.</h2>
            <p className="mt-3 text-slate-300 max-w-2xl">Short, practical courses built from a completed 6-month AI Generalist fellowship. No theory-only lectures — every module ships something.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 font-medium text-[#0A0F1C]" style={{backgroundColor:"#7DDFA0"}}>Join the interest list <ArrowRight size={16} /></Link>
        </div>
      </Reveal>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ACADEMY_COURSES.map((c, i) => (
          <Reveal key={c.name} delay={i*0.04}>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 h-full hover:bg-white/[0.06] transition">
              <c.icon size={22} className="text-[#7DDFA0]" />
              <div className="mt-3 font-semibold">{c.name}</div>
              <p className="mt-1 text-sm text-slate-400 leading-snug">{c.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-500">Next batch dates announced to the interest list first.</p>
    </div>
  </section>
);

const MutualFundsStrip = () => (
  <section className="bg-white border-t border-gray-200" data-testid="mutual-funds-strip">
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Also from Aumonline</div>
        <p className="mt-1 text-base text-[#1A1A1A]">Mutual fund distribution — our original business. AMFI-registered, {COMPANY.arn}, {COMPANY.aum} under management.</p>
      </div>
      <Link to="/mutual-funds" className="inline-flex items-center gap-2 font-medium whitespace-nowrap hover:gap-3 transition-all duration-150" style={{ color: COLORS.blue }}>View mutual fund services <ArrowRight size={16} /></Link>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Pillars />
      <Practitioner />
      <Solutions />
      <Bespoke />
      <Academy />
      <MutualFundsStrip />
    </div>
  );
}
